import React from "react";
import { motion } from "framer-motion";
import { Send, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import SenderCard from "./SenderCard";

const SendersList = ({
  senderIds,
  currentSenderIds,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemsPerPage,
  handlePageChange,
  setShowAddForm,
  editingId,
  editValue,
  setEditValue,
  errors,
  setErrors,
  handleEditSenderId,
  cancelEditing,
  startEditing,
  copyToClipboard,
  openDeleteModal,
  getStatusColor,
}) => {
  const senderIdsArray = Array.isArray(senderIds) ? senderIds : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {senderIdsArray.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="py-16 text-center"
        >
          <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full">
            <Send className="w-12 h-12 text-purple-500" />
          </div>
          <h3 className="mb-3 text-2xl font-bold text-gray-900">
            No Sender IDs Yet
          </h3>
          <p className="mx-auto mb-8 max-w-md text-gray-600">
            Create your first sender ID to start personalizing your messages and build your brand identity
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-8 py-4 font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg transition-all duration-200 transform hover:from-purple-700 hover:to-blue-700 hover:shadow-xl hover:scale-105"
          >
            <Plus className="mr-2 w-5 h-5" />
            Add Your First Sender ID
          </button>
        </motion.div>
      ) : (
        <>
          {/* Grid Layout */}
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-3">
            {currentSenderIds.map((sender, index) => (
              <SenderCard
                key={sender.id}
                sender={sender}
                index={index}
                editingId={editingId}
                editValue={editValue}
                setEditValue={setEditValue}
                errors={errors}
                setErrors={setErrors}
                handleEditSenderId={handleEditSenderId}
                cancelEditing={cancelEditing}
                startEditing={startEditing}
                copyToClipboard={copyToClipboard}
                openDeleteModal={openDeleteModal}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>

          {/* Pagination */}
          {senderIdsArray.length > itemsPerPage && (
            <div className="flex flex-col gap-4 justify-between items-center pt-6 border-t sm:flex-row border-gray-200/50">
              <div className="flex order-2 gap-2 items-center text-sm text-gray-600 sm:order-1">
                <span>Showing</span>
                <span className="font-semibold text-gray-900">
                  {startIndex + 1}-{Math.min(endIndex, senderIdsArray.length)}
                </span>
                <span>of</span>
                <span className="font-semibold text-gray-900">
                  {senderIdsArray.length}
                </span>
                <span>sender IDs</span>
              </div>

              <div className="flex order-1 gap-2 items-center sm:order-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-4 py-2.5 border border-gray-300/50 rounded-xl text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-gray-50/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 touch-manipulation shadow-sm"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>

                <div className="flex gap-1 items-center">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-xl transition-all duration-200 touch-manipulation ${
                          currentPage === page
                            ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg"
                            : "text-gray-700 hover:bg-gray-100/80 border border-gray-300/50 bg-white/80 backdrop-blur-sm"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-4 py-2.5 border border-gray-300/50 rounded-xl text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-gray-50/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 touch-manipulation shadow-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SendersList; 